// TemplatesCatalogAdminPage.js — the catalog-of-promoted-templates view
// at /admin/templates. Companion to TemplateCandidatesAdminPage:
//
//   - candidate page = inbox (queue triage, screen, decide)
//   - this page     = catalog (browse what's live, replace, demote)
//
// Lists every template currently committed under mchatai-source/harness/
// templates/<subdir>/<slug>/. Each card shows displayName, slug, category,
// extracted-from URL, GitHub link, and lazy-loaded iframe preview.
//
// Two destructive admin actions:
//   - Replace from candidate (linked over to /admin/template-candidates with
//     pre-filled targetSlug = this template's slug) — lets admin pick a
//     replacement from the queue.
//   - Demote (calls demoteTemplate Cloud Function with confirm dialog).
//
// Read access uses the public GitHub APIs via mchataiSourceTemplatesAPI.
// Demote uses the admin-only Cloud Function with PAT auth.

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { fetchPromotedTemplates } from './lib/mchataiSourceTemplatesAPI';
import './App.css';

const ADMIN_UIDS = ['GI1GHQ7wpLQKjJyf9QRQATyw5e33', 'vGB6BL3iJfh32aWZ0HA93nUV2013'];

export default function TemplatesCatalogAdminPage() {
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLog, setActionLog] = useState([]);
  const [busy, setBusy] = useState(null); // { kind, slug }
  const [filter, setFilter] = useState('');
  const [previewSlug, setPreviewSlug] = useState(null);

  useEffect(() => {
    const auth = getAuth();
    const unsub = auth.onAuthStateChanged(async (u) => {
      setUser(u);
      if (u && (ADMIN_UIDS.includes(u.uid) || (await u.getIdTokenResult()).claims?.role === 'admin')) {
        setIsAdmin(true);
      } else {
        setIsAdmin(false);
        setLoading(false);
      }
    });
    return () => unsub();
  }, []);

  const refresh = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    try {
      const list = await fetchPromotedTemplates();
      setTemplates(list);
    } catch (err) {
      pushLog('error', `Failed to load templates: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => { if (isAdmin) refresh(); }, [isAdmin, refresh]);

  function pushLog(kind, message) {
    setActionLog(prev => [{ ts: new Date().toLocaleTimeString(), kind, message }, ...prev].slice(0, 20));
  }

  async function handleDemote(t) {
    const confirmed = window.confirm(
      `Demote "${t.slug}" — permanently remove from mchatai-source main?\n\n` +
      `This commits a deletion to:\n  ${t.githubURL}\n\n` +
      `Future wizard runs that would have used this template as a seed will instead generate from scratch (or pick a different match if one exists).\n\n` +
      `OK to proceed?`
    );
    if (!confirmed) return;
    const reason = window.prompt('Reason (optional, used in commit message):', '') || '';
    setBusy({ kind: 'demote', slug: t.slug });
    try {
      const fn = httpsCallable(getFunctions(), 'demoteTemplate');
      const result = await fn({
        slug: t.slug,
        subdir: t.subdir,
        confirmKey: `delete-${t.slug}`,
        reason
      });
      pushLog('ok', `Demoted ${t.slug} → ${result.data.commitURL}`);
      await refresh();
    } catch (err) {
      pushLog('error', `Demote ${t.slug} failed: ${err.message}`);
    } finally {
      setBusy(null);
    }
  }

  if (!user) {
    return (
      <div className="mp-page" style={{ background: '#0f172a', color: '#f8fafc', textAlign: 'center', padding: '80px 24px' }}>
        <h2>Sign in required</h2>
        <Link to="/" style={{ color: '#3b82f6' }}>← Home</Link>
      </div>
    );
  }
  if (!isAdmin) {
    return (
      <div className="mp-page" style={{ background: '#0f172a', color: '#f8fafc', textAlign: 'center', padding: '80px 24px' }}>
        <h2>Access denied</h2>
        <p style={{ color: '#94a3b8' }}>This page is restricted to mChatAI admins.</p>
        <Link to="/account" style={{ color: '#3b82f6' }}>← Account</Link>
      </div>
    );
  }

  const filterLower = filter.trim().toLowerCase();
  const visible = filterLower
    ? templates.filter(t =>
        t.slug.toLowerCase().includes(filterLower) ||
        (t.manifest?.displayName || '').toLowerCase().includes(filterLower) ||
        (t.manifest?.category || '').toLowerCase().includes(filterLower) ||
        (t.manifest?.matchKeywords || []).some(k => k.toLowerCase().includes(filterLower))
      )
    : templates;

  // Group by subdir/category for nicer browsing.
  const grouped = {};
  for (const t of visible) {
    const cat = t.manifest?.category || t.subdir || 'other';
    grouped[cat] = grouped[cat] || [];
    grouped[cat].push(t);
  }
  const groupKeys = Object.keys(grouped).sort();

  return (
    <div className="mp-page" style={{ background: '#0f172a', color: '#f8fafc', minHeight: '100vh' }}>
      <section style={{ maxWidth: 1200, margin: '0 auto', padding: '40px 24px' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
          <h1 style={{ color: '#f8fafc', fontSize: '2rem', fontWeight: 800, margin: 0 }}>Templates Catalog</h1>
          <div style={{ display: 'flex', gap: 12, alignItems: 'baseline' }}>
            <Link to="/admin/template-candidates" style={{ color: '#94a3b8', fontSize: '0.85rem' }}>← Candidates</Link>
            <Link to="/admin" style={{ color: '#94a3b8', fontSize: '0.85rem' }}>Admin Dashboard</Link>
          </div>
        </div>
        <p style={{ color: '#64748b', marginBottom: 20, fontSize: '0.9rem' }}>
          Live shape of <code>mchatai-source/harness/templates/</code>. Currently {templates.length} promoted template{templates.length === 1 ? '' : 's'}.
          Use this view to spot a duplicate when a better candidate arrives, or to demote a template that's been superseded.
        </p>

        {/* Search + refresh */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
          <input
            type="search" placeholder="Filter by slug / name / category / keyword…"
            value={filter} onChange={(e) => setFilter(e.target.value)}
            style={{
              flex: 1, padding: '10px 14px', borderRadius: 8, border: '1px solid #334155',
              background: '#0f172a', color: '#f8fafc', fontSize: '0.85rem', fontFamily: 'inherit', outline: 'none'
            }}
          />
          <button onClick={refresh} disabled={loading} style={{
            background: '#1e293b', color: '#94a3b8', border: '1px solid #334155',
            padding: '8px 18px', borderRadius: 8, fontWeight: 500, fontSize: '0.85rem',
            cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'inherit', opacity: loading ? 0.5 : 1
          }}>
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>

        {loading ? (
          <div style={{ color: '#94a3b8', padding: 60, textAlign: 'center' }}>Loading templates from GitHub…</div>
        ) : visible.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', background: '#1e293b', borderRadius: 8, border: '1px dashed #334155' }}>
            <p style={{ color: '#94a3b8', margin: 0 }}>
              {filter
                ? `No templates match "${filter}".`
                : 'No promoted templates yet. Promote candidates from the Candidates page to populate the catalog.'}
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            {groupKeys.map(group => (
              <div key={group}>
                <h2 style={{ color: '#94a3b8', fontSize: '0.85rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, margin: '0 0 12px', paddingBottom: 4, borderBottom: '1px solid #1e293b' }}>
                  {group} <span style={{ color: '#475569', fontWeight: 500 }}>({grouped[group].length})</span>
                </h2>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(420px, 1fr))', gap: 12 }}>
                  {grouped[group].map(t => (
                    <TemplateCard
                      key={`${t.subdir}/${t.slug}`}
                      template={t}
                      previewOpen={previewSlug === t.slug}
                      onTogglePreview={() => setPreviewSlug(previewSlug === t.slug ? null : t.slug)}
                      onDemote={() => handleDemote(t)}
                      busy={busy}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {actionLog.length > 0 && (
          <div style={{ marginTop: 32, padding: 16, background: '#1e293b', borderRadius: 8, border: '1px solid #334155' }}>
            <h3 style={{ color: '#94a3b8', fontSize: '0.8rem', fontWeight: 600, textTransform: 'uppercase', margin: '0 0 12px' }}>Recent Actions</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.8rem', fontFamily: 'ui-monospace, monospace' }}>
              {actionLog.map((entry, i) => (
                <div key={i} style={{ color: entry.kind === 'error' ? '#f87171' : '#a3e635' }}>
                  <span style={{ color: '#475569', marginRight: 8 }}>{entry.ts}</span>
                  {entry.message}
                </div>
              ))}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

function TemplateCard({ template, previewOpen, onTogglePreview, onDemote, busy }) {
  const t = template;
  const m = t.manifest || {};
  const isBusy = busy?.slug === t.slug;
  const replaceURL = `/admin/template-candidates?targetSlug=${encodeURIComponent(t.slug)}&targetSubdir=${encodeURIComponent(t.subdir)}`;

  return (
    <div style={{
      background: '#1e293b', borderRadius: 8, padding: 14,
      border: '1px solid #334155', display: 'flex', flexDirection: 'column', gap: 8
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ color: '#f8fafc', fontWeight: 700, fontSize: '0.95rem' }}>
          {m.displayName || t.slug}
        </span>
        <code style={{ color: '#94a3b8', fontSize: '0.72rem' }}>{t.slug}</code>
      </div>

      {m.description && (
        <p style={{ color: '#cbd5e1', fontSize: '0.78rem', lineHeight: 1.4, margin: 0 }}>
          {m.description}
        </p>
      )}

      {m.matchKeywords?.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {m.matchKeywords.slice(0, 8).map((k, i) => (
            <span key={i} style={{
              fontSize: '0.66rem', padding: '2px 6px',
              background: '#0f172a', border: '1px solid #334155', borderRadius: 3,
              color: '#94a3b8'
            }}>{k}</span>
          ))}
          {m.matchKeywords.length > 8 && (
            <span style={{ fontSize: '0.66rem', color: '#475569' }}>+{m.matchKeywords.length - 8}</span>
          )}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, fontSize: '0.7rem', color: '#475569', alignItems: 'center', flexWrap: 'wrap' }}>
        {m.artifactType && <span>{m.artifactType}</span>}
        {m.extractedAt && <span>· promoted {m.extractedAt}</span>}
        {m.sourceURL && (
          <a href={m.sourceURL} target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6', textDecoration: 'none' }}>
            · source ↗
          </a>
        )}
        <a href={t.githubURL} target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6', textDecoration: 'none' }}>
          · github ↗
        </a>
      </div>

      {previewOpen && (
        <iframe
          src={t.referenceURL}
          title={`Preview ${t.slug}`}
          sandbox="allow-scripts allow-same-origin"
          style={{ width: '100%', height: 280, border: '1px solid #334155', borderRadius: 6, background: '#000' }}
        />
      )}

      <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
        <button onClick={onTogglePreview}
          style={cardBtn('#334155', '#f8fafc', false)}>
          {previewOpen ? 'Hide preview' : 'Preview'}
        </button>
        <Link to={replaceURL}
          style={{
            ...cardBtn('#3b82f6', '#fff', false),
            textDecoration: 'none', display: 'inline-block', textAlign: 'center'
          }}
          title="Open the candidate queue with this slug pre-filled — pick a replacement from there">
          Replace from candidate ↗
        </Link>
        <button onClick={onDemote} disabled={isBusy}
          style={cardBtn('#7f1d1d', '#fee2e2', isBusy)}
          title="Permanently remove this template from mchatai-source main">
          {isBusy && busy.kind === 'demote' ? 'Demoting…' : 'Demote'}
        </button>
      </div>
    </div>
  );
}

function cardBtn(bg, fg, disabled) {
  return {
    background: bg, color: fg, border: 'none',
    padding: '6px 12px', borderRadius: 4,
    fontSize: '0.72rem', fontWeight: 600,
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontFamily: 'inherit', opacity: disabled ? 0.5 : 1, whiteSpace: 'nowrap'
  };
}
